import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Layout } from './components/Layout.js';
import { ScrollToTop } from './components/ScrollToTop.js';
import { RequireAuth } from './components/RequireAuth.js';
import { HomePage } from './pages/Home.js';
import { LoginPage } from './pages/Login.js';
import { AuthCallbackPage } from './pages/AuthCallback.js';
import { NotFoundPage } from './pages/NotFound.js';
import { AccountPage } from './pages/Account.js';
import { AccountListingsPage } from './pages/AccountListings.js';
import { AccountPayoutsPage } from './pages/AccountPayouts.js';
import { AccountEarningsPage } from './pages/AccountEarnings.js';
import { ListYourTrailerPage } from './pages/ListYourTrailer.js';
import { ListingDetailPage } from './pages/ListingDetail.js';
import { InboxPage } from './pages/Inbox.js';
import { WelcomePage } from './pages/Welcome.js';
import { GuaranteePage } from './pages/Guarantee.js';
import { InsurancePage } from './pages/Insurance.js';
import { IdVerificationPage } from './pages/IdVerification.js';
import { AccountVerifyPage } from './pages/AccountVerify.js';
import { TermsPage } from './pages/Terms.js';
import { PrivacyPage } from './pages/Privacy.js';
import { AboutPage } from './pages/About.js';
import { ContactPage } from './pages/Contact.js';
import { HowItWorksPage } from './pages/HowItWorks.js';
import { CityPage } from './pages/CityPage.js';
import { NeighbourhoodPage } from './pages/NeighbourhoodPage.js';
import { RequireAdmin } from './components/RequireAdmin.js';

// Heavy pages (Mapbox + listing form) lazy-loaded
const SearchPage = lazy(() =>
  import('./pages/Search.js').then((m) => ({ default: m.SearchPage })),
);
const ListingNewPage = lazy(() =>
  import('./pages/ListingNew.js').then((m) => ({ default: m.ListingNewPage })),
);
const ListingEditPage = lazy(() =>
  import('./pages/ListingEdit.js').then((m) => ({ default: m.ListingEditPage })),
);
const CheckoutPage = lazy(() =>
  import('./pages/Checkout.js').then((m) => ({ default: m.CheckoutPage })),
);
const TransactionDetailPage = lazy(() =>
  import('./pages/TransactionDetail.js').then((m) => ({ default: m.TransactionDetailPage })),
);

const AdminLayout = lazy(() =>
  import('./pages/admin/AdminLayout.js').then((m) => ({ default: m.AdminLayout })),
);
const AdminOverviewPage = lazy(() =>
  import('./pages/admin/AdminOverview.js').then((m) => ({ default: m.AdminOverviewPage })),
);
const AdminListingsPage = lazy(() =>
  import('./pages/admin/AdminListings.js').then((m) => ({ default: m.AdminListingsPage })),
);
const AdminUsersPage = lazy(() =>
  import('./pages/admin/AdminUsers.js').then((m) => ({ default: m.AdminUsersPage })),
);
const AdminTransactionsPage = lazy(() =>
  import('./pages/admin/AdminTransactions.js').then((m) => ({
    default: m.AdminTransactionsPage,
  })),
);
const AdminDisputesPage = lazy(() =>
  import('./pages/admin/AdminDisputes.js').then((m) => ({ default: m.AdminDisputesPage })),
);
const AdminJobsPage = lazy(() =>
  import('./pages/admin/AdminJobs.js').then((m) => ({ default: m.AdminJobsPage })),
);

function PageFallback() {
  return <div className="mx-auto max-w-md px-4 py-24 text-center text-navy-500">Loading…</div>;
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

export function App() {
  return (
    <>
      <ScrollToTop />
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route
          path="/welcome"
          element={
            <RequireAuth allowUnonboarded>
              <WelcomePage />
            </RequireAuth>
          }
        />
        <Route path="/list-your-trailer" element={<ListYourTrailerPage />} />
        <Route
          path="/search"
          element={
            <Lazy>
              <SearchPage />
            </Lazy>
          }
        />
        <Route path="/listings/:slug" element={<ListingDetailPage />} />
        <Route path="/trailer-rental/:city" element={<CityPage />} />
        <Route
          path="/trailer-rental/:city/:neighbourhood"
          element={<NeighbourhoodPage />}
        />

        <Route path="/guarantee" element={<GuaranteePage />} />
        <Route path="/insurance" element={<InsurancePage />} />
        <Route path="/id-verification" element={<IdVerificationPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        <Route
          path="/account"
          element={
            <RequireAuth>
              <AccountPage />
            </RequireAuth>
          }
        />
        <Route
          path="/account/verify"
          element={
            <RequireAuth>
              <AccountVerifyPage />
            </RequireAuth>
          }
        />

        <Route
          path="/inbox"
          element={
            <RequireAuth>
              <InboxPage />
            </RequireAuth>
          }
        />
        <Route
          path="/transactions/:id"
          element={
            <RequireAuth>
              <Lazy>
                <TransactionDetailPage />
              </Lazy>
            </RequireAuth>
          }
        />
        <Route
          path="/checkout/:id"
          element={
            <RequireAuth>
              <Lazy>
                <CheckoutPage />
              </Lazy>
            </RequireAuth>
          }
        />
        <Route
          path="/account/listings"
          element={
            <RequireAuth>
              <AccountListingsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/account/payouts"
          element={
            <RequireAuth>
              <AccountPayoutsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/account/earnings"
          element={
            <RequireAuth>
              <AccountEarningsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/listings/new"
          element={
            <RequireAuth>
              <Lazy>
                <ListingNewPage />
              </Lazy>
            </RequireAuth>
          }
        />
        <Route
          path="/listings/:id/edit"
          element={
            <RequireAuth>
              <Lazy>
                <ListingEditPage />
              </Lazy>
            </RequireAuth>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Admin routes — outside the main Layout, gated by RequireAdmin */}
      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <Lazy>
              <AdminLayout />
            </Lazy>
          </RequireAdmin>
        }
      >
        <Route
          index
          element={
            <Lazy>
              <AdminOverviewPage />
            </Lazy>
          }
        />
        <Route
          path="listings"
          element={
            <Lazy>
              <AdminListingsPage />
            </Lazy>
          }
        />
        <Route
          path="users"
          element={
            <Lazy>
              <AdminUsersPage />
            </Lazy>
          }
        />
        <Route
          path="transactions"
          element={
            <Lazy>
              <AdminTransactionsPage />
            </Lazy>
          }
        />
        <Route
          path="disputes"
          element={
            <Lazy>
              <AdminDisputesPage />
            </Lazy>
          }
        />
        <Route
          path="jobs"
          element={
            <Lazy>
              <AdminJobsPage />
            </Lazy>
          }
        />
      </Route>
    </Routes>
    </>
  );
}
